/**
 * Passkey (WebAuthn) management for an enrolled user (profile security card).
 *
 * Lists registered passkeys and supports adding one (register ceremony,
 * gated on a TOTP step-up code + a human label) and removing one (also
 * gated on a step-up code). The step-up code travels in the request body,
 * never the URL.
 */
import { useCallback, useEffect, useState } from 'react';
import authService, { type WebAuthnCredential } from '../../services/auth.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import {
  preformatCreationOptions,
  serializeAttestation,
} from '../../lib/webauthn';
import { colors } from '../../styles/theme';

function webauthnSupported(): boolean {
  return globalThis.PublicKeyCredential !== undefined;
}

export default function PasskeysManager(): JSX.Element {
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // Add form
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [addCode, setAddCode] = useState('');
  const [busy, setBusy] = useState(false);
  // Per-row removal step-up
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [removeCode, setRemoveCode] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCredentials(await authService.listWebAuthnCredentials());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load passkeys.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const mapMfaError = (e: unknown, fallback: string): string => {
    if (getApiErrorCode(e) === 'mfa_not_enrolled') {
      return 'Turn on two-factor authentication first.';
    }
    return getApiErrorMessage(e, fallback);
  };

  const addPasskey = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const optionsJson = await authService.getWebAuthnRegisterOptions(addCode.trim());
      const publicKey = preformatCreationOptions(
        optionsJson as unknown as Parameters<typeof preformatCreationOptions>[0],
      );
      const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
      if (!credential) {
        setError('Passkey registration was cancelled.');
        return;
      }
      await authService.verifyWebAuthnRegistration(serializeAttestation(credential), label.trim());
      setAdding(false);
      setLabel('');
      setAddCode('');
      setFlash('Passkey added.');
      globalThis.setTimeout(() => setFlash(null), 5000);
      await reload();
    } catch (e: unknown) {
      if (e instanceof DOMException) {
        setError('Passkey registration did not complete. Please try again.');
      } else {
        setError(mapMfaError(e, 'Could not add the passkey.'));
      }
    } finally {
      setBusy(false);
    }
  }, [addCode, label, reload]);

  const removePasskey = useCallback(async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await authService.removeWebAuthnCredential(id, removeCode.trim());
      setPendingId(null);
      setRemoveCode('');
      setFlash('Passkey removed.');
      globalThis.setTimeout(() => setFlash(null), 5000);
      await reload();
    } catch (e: unknown) {
      setError(mapMfaError(e, 'Could not remove the passkey.'));
    } finally {
      setBusy(false);
    }
  }, [removeCode, reload]);

  return (
    <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
      <div className="text-sm font-medium mb-1" style={{ color: colors.ink }}>
        Passkeys
      </div>
      <p className="text-xs mb-3" style={{ color: colors.muted }}>
        Sign in with a device passkey (Face ID, Windows Hello, a security key)
        instead of typing a code.
      </p>

      {flash && (
        <div className="rounded-lg p-3 mb-3 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded-lg p-3 mb-3 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {!webauthnSupported() && (
        <p className="text-xs mb-3" style={{ color: colors.muted }}>
          This browser does not support passkeys.
        </p>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && credentials.length > 0 && (
        <ul className="space-y-2 mb-4">
          {credentials.map((c) => (
            <li key={c.id} className="p-3 rounded-lg" style={{ border: `1px solid ${colors.border}` }}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm" style={{ color: colors.ink }}>
                    {c.label || 'Passkey'}
                  </div>
                  <div className="text-xs" style={{ color: colors.muted }}>
                    Added {c.created_at.slice(0, 10)}
                    {c.last_used_at ? ` · last used ${c.last_used_at.slice(0, 10)}` : ' · never used'}
                  </div>
                </div>
                {pendingId !== c.id && (
                  <button
                    type="button"
                    onClick={() => { setError(null); setRemoveCode(''); setPendingId(c.id); }}
                    className="text-xs px-2 py-1 rounded-md"
                    style={{ background: 'transparent', color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}
                  >
                    Remove
                  </button>
                )}
              </div>

              {pendingId === c.id && (
                <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <p className="text-xs mb-2" style={{ color: colors.muted }}>
                    Enter the current 6-digit code from your authenticator app (the
                    same app you use to sign in) to remove this passkey.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={removeCode}
                      onChange={(e) => setRemoveCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder="Authenticator code"
                      className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
                      style={{ background: colors.surface, color: colors.ink, border: `1px solid ${colors.border}` }}
                    />
                    <button
                      type="button"
                      onClick={() => { removePasskey(c.id); }}
                      disabled={busy || removeCode.trim().length < 6}
                      className="text-xs px-3 py-2 rounded-md font-semibold"
                      style={{ background: colors.danger, color: '#fff' }}
                    >
                      {busy ? 'Removing…' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPendingId(null); setRemoveCode(''); }}
                      disabled={busy}
                      className="text-xs px-3 py-2 rounded-md"
                      style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!loading && credentials.length === 0 && (
        <p className="text-sm mb-3" style={{ color: colors.muted }}>No passkeys registered yet.</p>
      )}

      {webauthnSupported() && !adding && (
        <button
          type="button"
          onClick={() => { setError(null); setLabel(''); setAddCode(''); setAdding(true); }}
          className="text-sm px-3 py-1.5 rounded-lg font-semibold"
          style={{ background: colors.canvas, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Add a passkey
        </button>
      )}

      {adding && (
        <div className="p-3 rounded-lg" style={{ border: `1px solid ${colors.border}` }}>
          <p className="text-xs mb-2" style={{ color: colors.muted }}>
            Name this passkey, then confirm with the current 6-digit code from
            your authenticator app (Google Authenticator, 1Password, etc. — the
            same code you use to sign in; not a code sent by email or SMS).
            Your browser will then prompt you to create the passkey.
          </p>
          <div className="flex flex-col gap-2">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 64))}
              placeholder="e.g. MacBook Touch ID"
              className="px-3 py-2 rounded-lg text-sm"
              style={{ background: colors.surface, color: colors.ink, border: `1px solid ${colors.border}` }}
            />
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={addCode}
                onChange={(e) => setAddCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Authenticator code"
                className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
                style={{ background: colors.surface, color: colors.ink, border: `1px solid ${colors.border}` }}
              />
              <button
                type="button"
                onClick={() => { addPasskey(); }}
                disabled={busy || addCode.trim().length < 6 || label.trim().length === 0}
                className="text-sm px-3 py-2 rounded-lg font-semibold"
                style={{ background: colors.gold, color: colors.navy }}
              >
                {busy ? 'Adding…' : 'Add passkey'}
              </button>
              <button
                type="button"
                onClick={() => { setAdding(false); setLabel(''); setAddCode(''); setError(null); }}
                disabled={busy}
                className="text-sm px-3 py-2 rounded-lg"
                style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
